/*
 * Cadwork webgl Engine
 * http://www.lexoview.com
 *
 * Copyright (C) 2015 Cadwork Informatik AG, Basel, Switzerland
 * Dual licensed under the MIT and GPL
 *
 * @author Jaroslav Přibyl (pribyl@cadwork.cz)
 *
 */
import TerrainTileAddress from "./terrain_tile_address";
import HeightMapTile from "../elements/height_map_tile";
import TesselatedArea, {TTesselatedArea} from "./tesselated_area";
import ITerrain from "./terrain_interface";

export default
class TerrainTile {

    /**
     * To keep tiles stitching at different level of detail, we need the
     * suze of the tiles to be (2 * n) - 1 (e.g. 2, 3, 5, 9, 17, 33, ...)
     * @type {{cols: number, rows: number}}
     */
    public static TILE_SIZE: { cols: number, rows: number } = {
        cols: 17,
        rows: 17
    };

    private terrainRef: ITerrain;
    private address: TerrainTileAddress;
    private children: TerrainTile[];
    private parent: TerrainTile;

    public heightMapTile: HeightMapTile;

    constructor(aTerrain: ITerrain, aAddress: TerrainTileAddress) {
        this.terrainRef = aTerrain;

        this.address = aAddress;
        this.children = [null, null, null, null];

        this.heightMapTile = null;

        this.parent = null;
    }

    public addDataTile(aDataTile: any, aHeightMapTile: HeightMapTile) {
        if (this.address.level !== aDataTile.level.zoomLevel) {
            // create tile address from the data tile
            let dataTileAddress = new TerrainTileAddress(aDataTile.x, aDataTile.y, aDataTile.level.zoomLevel);

            // get address of next level tile
            let childAddress = dataTileAddress.atLevel(this.address.level + 1);
            let index = childAddress.getIndexToChildren();
            let child = this.children[index];
            if (!child) {
                child = new TerrainTile(this.terrainRef, childAddress);
                child.parent = this;
                this.children[index] = child;
            }

            child.addDataTile(aDataTile, aHeightMapTile);
        }
        else {
            this.heightMapTile = aHeightMapTile;
        }
    }

    /**
     * Complete the tree so that each tile has all four children defined or no children defined
     */
    public completeChildren() {
        for (let i = 0; i < this.children.length; i++) {
            let child = this.children[i];
            if (child) {
                child.completeChildren();
            } else {
                // we need to get address of the child i.e. we need to get higher detail for current tile address
                let childAddress = this.address.atLevel(this.address.level + 1);

                // determine the 2D child index
                let x = i % 2;
                let y = Math.floor(i / 2);

                childAddress.x += x;
                childAddress.y += y;

                child = new TerrainTile(this.terrainRef, childAddress);
                child.parent = this;
                child.heightMapTile = this.heightMapTile;
                this.children[i] = child;
            }
        }
    }

    /***
     * Copy the heightmap tiles reference from parent to child nodes which has not its own heightmap data available
     * @param aHeightMapTile
     */
    public createReferencesToHeightMapTiles(aHeightMapTile: HeightMapTile) {
        for (let i = 0; i < this.children.length; i++) {
            let child = this.children[i];
            if (child) {
                if (!this.heightMapTile) {
                    this.heightMapTile = aHeightMapTile;
                }
                child.createReferencesToHeightMapTiles(this.heightMapTile);
            }
        }
    }

    public hasChild(): boolean {
        let hasChild = true;
        for (let i = 0; i < this.children.length; i++) {
            hasChild = hasChild && (this.children[i] !== null);
        }

        return hasChild;
    }

    public computeTesselatedAreas() {
        if (this.hasChild()) {
            for (let i = 0; i < this.children.length; i++) {
                let child = this.children[i];
                child.computeTesselatedAreas();
            }
        } else {
            if (this.heightMapTile) {
                this.heightMapTile.addTesselationArea(this.getTesselatedArea());
            }
        }
    }

    public getNeighbourTile(aOffsetX: number, aOffsetY: number): TerrainTile {
        let addr = this.address.atLevel(this.address.level); // just copy current address
        addr.x += aOffsetX;
        addr.y += aOffsetY;

        return this.terrainRef.getRoot().getTileByAddress(addr);
    }

    public getParent(): TerrainTile {
        return this.parent;
    }

    public getParentWithHeightMapTileAtLevel(aLevel: number): TerrainTile {
        let currTile: TerrainTile = this;
        while (currTile) {
            if (currTile.heightMapTile) {
                if (currTile.heightMapTile.getDataTile().level.zoomLevel <= aLevel &&
                    currTile.address.level <= aLevel) {
                    return currTile;
                }
            }
            currTile = currTile.getParent();
        }

        return null;
    }

    public getTesselatedArea(): TTesselatedArea {
        let dataTile = this.heightMapTile.getDataTile();
        let dataTileAddress =
            new TerrainTileAddress(
                dataTile.x,
                dataTile.y,
                dataTile.level.zoomLevel
            );

        // transform this tile address to heightmap level in order to get the place where this tile is placed on the heightmap
        let floatAddress = this.address.atLevelFloat(dataTileAddress.level);

        let factorToGetAtLevel = dataTileAddress.getFactorToGetAtLevel(this.address.level);
        let offsetX = Math.floor((floatAddress.x % 1) * TerrainTile.TILE_SIZE.cols);
        let offsetY = Math.floor((floatAddress.y % 1) * TerrainTile.TILE_SIZE.rows);

        let sizeX = Math.ceil((TerrainTile.TILE_SIZE.cols / factorToGetAtLevel));
        let sizeY = Math.ceil((TerrainTile.TILE_SIZE.rows / factorToGetAtLevel));

        return TesselatedArea(
            dataTile.level,
            offsetX,
            offsetY,
            sizeX,
            sizeY
        );
    }

    public getTesselatedAreaInHeightMapTile(aHeightmapTile: HeightMapTile): TTesselatedArea{
        let dataTile = aHeightmapTile.getDataTile();

        let dataTileAddress =
            new TerrainTileAddress(
                dataTile.x,
                dataTile.y,
                dataTile.level.zoomLevel
            );

        // transform this tile address to heightmap level in order to get the place where this tile is placed on the heightmap
        let floatAddress = this.address.atLevelFloat(dataTileAddress.level);

        let factorToGetAtLevel = dataTileAddress.getFactorToGetAtLevel(this.address.level);
        let offsetX = Math.floor((floatAddress.x % 1) * TerrainTile.TILE_SIZE.cols);
        let offsetY = Math.floor((floatAddress.y % 1) * TerrainTile.TILE_SIZE.rows);

        let sizeX = Math.ceil((TerrainTile.TILE_SIZE.cols / factorToGetAtLevel));
        let sizeY = Math.ceil((TerrainTile.TILE_SIZE.rows / factorToGetAtLevel));

        return TesselatedArea(
            dataTile.level,
            offsetX,
            offsetY,
            sizeX,
            sizeY
        );
    }

    public stitchLeftSide() {
        let neigbhorTile = this.getNeighbourTile(-1, 0);
        if (!neigbhorTile) {
            return false;
        }

        if (!neigbhorTile.heightMapTile) {
            return false;
        }

        if (!this.heightMapTile) {
            return false;
        }

        let neighborHeightMapLevel = neigbhorTile.heightMapTile.getDataTile().level.zoomLevel;
        let thisHeightMapLevel = this.heightMapTile.getDataTile().level.zoomLevel;

        let factor = Math.pow(2.0, thisHeightMapLevel - neighborHeightMapLevel);
        if (factor > 1) {

            let superParent = this.getParentWithHeightMapTileAtLevel(neighborHeightMapLevel);
            if (!superParent) {
                return false;
            }
            let superParentTesselatedArea = this.getTesselatedAreaInHeightMapTile(superParent.heightMapTile);

            // pass through tesselated area of this tile
            let thisTesselatedArea = this.getTesselatedArea();
            let col = thisTesselatedArea.offset.x;
            for (let row = thisTesselatedArea.offset.y; row < (thisTesselatedArea.offset.y + thisTesselatedArea.size.rows); row++) {
                let superParentCol = superParentTesselatedArea.offset.x + ((col - thisTesselatedArea.offset.x) / factor);
                let superParentRow = superParentTesselatedArea.offset.y + ((row - thisTesselatedArea.offset.y) / factor);

                let heightA = superParent.heightMapTile.getHeightWithoutOffset(Math.floor(superParentCol), Math.floor(superParentRow));
                let heightB = superParent.heightMapTile.getHeightWithoutOffset(Math.floor(superParentCol), Math.ceil(superParentRow));

                let height = heightA + (heightB - heightA) * ((superParentRow) % 1);
                this.heightMapTile.setHeight(col, row, height);
            }
        }
    }

    public stitchRightSide() {
        let neigbhorTile = this.getNeighbourTile(1, 0);
        if (!neigbhorTile) {
            return false;
        }

        if (!neigbhorTile.heightMapTile) {
            return false;
        }

        if (!this.heightMapTile) {
            return false;
        }

        let neighborHeightMapLevel = neigbhorTile.heightMapTile.getDataTile().level.zoomLevel;
        let thisHeightMapLevel = this.heightMapTile.getDataTile().level.zoomLevel;

        let factor = Math.pow(2.0, thisHeightMapLevel - neighborHeightMapLevel);
        if (factor > 1) {

            let superParent = this.getParentWithHeightMapTileAtLevel(neighborHeightMapLevel);
            if (!superParent) {
                return false;
            }
            let superParentTesselatedArea = this.getTesselatedAreaInHeightMapTile(superParent.heightMapTile);

            // pass through tesselated area of this tile
            let thisTesselatedArea = this.getTesselatedArea();
            let col = thisTesselatedArea.offset.x + thisTesselatedArea.size.cols - 1;
            for (let row = thisTesselatedArea.offset.y; row < thisTesselatedArea.offset.y + thisTesselatedArea.size.rows; row++) {
                let superParentCol = superParentTesselatedArea.offset.x + ((col - thisTesselatedArea.offset.x) / factor);
                let superParentRow = superParentTesselatedArea.offset.y + ((row - thisTesselatedArea.offset.y) / factor);

                let heightA = superParent.heightMapTile.getHeightWithoutOffset(Math.floor(superParentCol), Math.floor(superParentRow));
                let heightB = superParent.heightMapTile.getHeightWithoutOffset(Math.floor(superParentCol), Math.ceil(superParentRow));

                let height = heightA + (heightB - heightA) * ((superParentRow) % 1);
                this.heightMapTile.setHeight(col, row, height);
            }
        }
    }

    public stitchTopSide() {
        let neigbhorTile = this.getNeighbourTile(0, -1);
        if (!neigbhorTile) {
            return false;
        }
        if (!neigbhorTile.heightMapTile) {
            return false;
        }
        if (!this.heightMapTile) {
            return false;
        }

        let neighborHeightMapLevel = neigbhorTile.heightMapTile.getDataTile().level.zoomLevel;
        let thisHeightMapLevel = this.heightMapTile.getDataTile().level.zoomLevel;

        let factor = Math.pow(2.0, thisHeightMapLevel - neighborHeightMapLevel);
        if (factor > 1) {

            let superParent = this.getParentWithHeightMapTileAtLevel(neighborHeightMapLevel);
            if (!superParent) {
                return false;
            }
            let superParentTesselatedArea = this.getTesselatedAreaInHeightMapTile(superParent.heightMapTile);

            // pass through tesselated area of this tile
            let thisTesselatedArea = this.getTesselatedArea();
            let row = thisTesselatedArea.offset.y;
            for (let col = thisTesselatedArea.offset.x; col < thisTesselatedArea.offset.x + thisTesselatedArea.size.cols; col++) {
                let superParentCol = superParentTesselatedArea.offset.x + ((col - thisTesselatedArea.offset.x) / factor);
                let superParentRow = superParentTesselatedArea.offset.y + ((row - thisTesselatedArea.offset.y) / factor);

                let heightA = superParent.heightMapTile.getHeightWithoutOffset(Math.floor(superParentCol), Math.floor(superParentRow));
                let heightB = superParent.heightMapTile.getHeightWithoutOffset(Math.ceil(superParentCol), Math.floor(superParentRow));

                let height = heightA + (heightB - heightA) * ((superParentCol) % 1);
                this.heightMapTile.setHeight(col, row, height);
            }
        }
    }

    public stitchBottomSide() {
        let neigbhorTile = this.getNeighbourTile(0, 1);
        if (!neigbhorTile) {
            return false;
        }
        if (!neigbhorTile.heightMapTile) {
            return false;
        }
        if (!this.heightMapTile) {
            return false;
        }

        let neighborHeightMapLevel = neigbhorTile.heightMapTile.getDataTile().level.zoomLevel;
        let thisHeightMapLevel = this.heightMapTile.getDataTile().level.zoomLevel;

        let factor = Math.pow(2.0, thisHeightMapLevel - neighborHeightMapLevel);
        if (factor > 1) {
            let superParent = this.getParentWithHeightMapTileAtLevel(neighborHeightMapLevel);
            if (!superParent) {
                return false;
            }
            let superParentTesselatedArea = this.getTesselatedAreaInHeightMapTile(superParent.heightMapTile);

            // pass through tesselated area of this tile
            let thisTesselatedArea = this.getTesselatedArea();
            let row = thisTesselatedArea.offset.y + thisTesselatedArea.size.rows - 1;
            for (let col = thisTesselatedArea.offset.x; col < thisTesselatedArea.offset.x + thisTesselatedArea.size.cols; col++) {
                let superParentCol = superParentTesselatedArea.offset.x + ((col - thisTesselatedArea.offset.x) / factor);
                let superParentRow = superParentTesselatedArea.offset.y + ((row - thisTesselatedArea.offset.y) / factor);

                let heightA = superParent.heightMapTile.getHeightWithoutOffset(Math.floor(superParentCol), Math.floor(superParentRow));
                let heightB = superParent.heightMapTile.getHeightWithoutOffset(Math.ceil(superParentCol), Math.floor(superParentRow));

                let height = heightA + (heightB - heightA) * ((superParentCol) % 1);
                this.heightMapTile.setHeight(col, row, height);
            }
        }
    }

    public getTileByAddress(aTileAddress: TerrainTileAddress): TerrainTile {
        if (this.address.level !== aTileAddress.level) {
            // get address of next level tile
            let childAddress = aTileAddress.atLevel(this.address.level + 1);
            let index = childAddress.getIndexToChildren();
            let child = this.children[index];
            if (!child) {
                return this;
            }
            return child.getTileByAddress(aTileAddress);
        } else {
            return this;
        }
    }

    public stitchTilesByChangingElevation() {
        // stitch
        // check left child
        this.stitchLeftSide();
        this.stitchRightSide();
        this.stitchTopSide();
        this.stitchBottomSide();

        for (let i = 0; i < this.children.length; i++) {
            let child = this.children[i];
            if (child) {
                child.stitchTilesByChangingElevation();
            }
        }
    }
}
